import React, { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { Navbar } from "./components/navbar";
import { MenuOverlay } from "./components/menuoverlay";
import { Landing } from "./landing/landingMain";
import { MyProject } from "./skills/skillsMain";
import { Education } from "./education/educationMain";

interface PageItem {
  component: React.ReactNode;
  threshold: number;
  topic: string;
  id: string;
}

export const App: React.FC = () => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const toggleMenu = () => setMenuIsOpen((prev) => !prev);

  const [currentLanguage, setCurrentLanguage] = useState(0);
  const toggleLanguage = () => setCurrentLanguage((prev) => (prev + 1) % 3);

  const [scrollY, setScrollY] = useState(0);
  const [currentTheme, setCurrentTheme] = useState(0);
  const [currentTopic, setCurrentTopic] = useState("");

  // scroll listener
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // theme (morning/afternoon/evening)
  useEffect(() => {
    const updateTheme = () => {
      const now = new Date();
      const thailandHours = new Date(
        now.getTime() + now.getTimezoneOffset() * 60000 + 7 * 60 * 60 * 1000
      ).getHours();

      if (thailandHours >= 5 && thailandHours < 12) setCurrentTheme(1);
      else if (thailandHours >= 12 && thailandHours < 18) setCurrentTheme(2);
      else setCurrentTheme(3);
    };
    updateTheme();
    const interval = setInterval(updateTheme, 60000);
    return () => clearInterval(interval);
  }, []);

  // define pages with useMemo to avoid effect dependency issue
  const pages: PageItem[] = useMemo(
    () => [
      {
        component: <Landing theme={currentTheme} />,
        threshold: 0,
        topic: "",
        id: "landing",
      },
      {
        component: <MyProject />,
        threshold: window.innerHeight * 0.8,
        topic: "Skills",
        id: "skill",
      },
      {
        component: <MyProject />,
        threshold: window.innerHeight * 1.5 + window.innerHeight * 1,
        topic: "Experiences",
        id: "experience",
      },
      {
        component: <MyProject />,
        threshold: window.innerHeight * 3 + window.innerHeight * 1,
        topic: "Projects",
        id: "project",
      },
      {
        component: <Education theme={currentTheme} />,
        threshold: window.innerHeight * 4.5 + window.innerHeight * 1,
        topic: "Education",
        id: "education",
      },
      {
        component: <Education theme={currentTheme} />,
        threshold: window.innerHeight * 6 + window.innerHeight * 1,
        topic: "Contact",
        id: "contact",
      },
    ],
    [currentTheme]
  );

  // update current topic & close menu when going back to landing
  useEffect(() => {
    let topic = "";
    for (let i = pages.length - 1; i >= 0; i--) {
      if (scrollY >= pages[i].threshold) {
        topic = pages[i].topic;
        break;
      }
    }
    setCurrentTopic(topic);

    if (scrollY < window.innerHeight * 0.8) {
      setMenuIsOpen(false);
    }
  }, [scrollY, pages]);

  const showNavbar = scrollY >= window.innerHeight * 0.8;

  return (
    <FixedPage theme={currentTheme}>
      {/* Navbar fixed at top */}
      <AnimatePresence>
        {showNavbar && (
          <>
            <FixedNavbar
              key="navbar"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <Navbar
                menuIsOpen={menuIsOpen}
                toggleMenu={toggleMenu}
                currentLanguage={currentLanguage}
                toggleLanguage={toggleLanguage}
                theme={currentTheme}
              />
            </FixedNavbar>

            <motion.div
              key="menuoverlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <MenuOverlay menuIsOpen={menuIsOpen} toggleMenu={toggleMenu} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {pages.map((page, i) => {
        const nextThreshold = pages[i + 1]?.threshold ?? Infinity;
        const isVisible = scrollY >= page.threshold && scrollY < nextThreshold;

        return (
          <MotionPage
            key={page.id}
            id={page.id}
            isLanding={page.id === "landing"}
            initial={{ opacity: 0 }}
            animate={{ opacity: isVisible ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {page.component}
          </MotionPage>
        );
      })}
      {currentTopic && <Topic>{currentTopic}</Topic>}
    </FixedPage>
  );
};

// ====================== styled ======================

const FixedPage = styled("div")<{ theme: number }>(({ theme }) => ({
  transition: "background 1s ease",
  color: theme === 3 ? "#fff" : "#000",
  backgroundColor:
    theme === 1 ? "#87CEEB" : theme === 2 ? "#FAFDD6" : "#2F1252",
}));

const FixedNavbar = styled(motion.div)({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  zIndex: 1001,
});

const MotionPage = styled(motion.div)<{ isLanding?: boolean }>(
  ({ isLanding }) => ({
    height: isLanding ? "120vh" : "150vh",
    paddingLeft: isLanding ? "0" : "5vw",
    paddingRight: isLanding ? "0" : "5vw",
  })
);

const Topic = styled("div")({
  position: "fixed",
  top: "10vh",
  right: "1vw",
  fontSize: "1.5rem",
  fontWeight: "bold",
  zIndex: 0,
});
